import { Address, Conditional, Content, Forms, Grid, Modals } from "components";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import { Divider, Shake } from "components";

import { assets, incomes } from "../../../utils";

import Button from "components/Button";
import { useDebounce } from "hooks/debounce";
import PartnerRegister from "./PartnerRegister";
import ResponsiblePersonRegister from "./ResponsiblePersonRegister";
import * as S from "./styles";

export const LegalEntity = (props: any) => {
  const { values, errors, setFieldValue } = useFormikContext<{
    address: {
      city: string;
      state: string;
      number: string;
      street: string;
      zipcode: string;
      complement: string;
      neighborhood: string;
    };
    partners: any[];
    responsible: any;
  }>();

  const [zipcode, setZipcode] = useState("");
  const debouncedZipcode = useDebounce(zipcode);

  const getAddress = async (editing?: boolean) => {
    const address: any = await Modals.Address({
      zipcode: debouncedZipcode || values.address.zipcode,
      ...(editing && { initialValues: values.address }),
    });

    if (!address) return;

    for (const [key, value] of Object.entries(address)) {
      setFieldValue(`address.${key}`, value);
    }
  };

  const add = async () => {
    const partner: any = await PartnerRegister();

    if (!partner) return;

    setFieldValue("partners", [
      ...values.partners,
      { id: Math.random(), ...partner },
    ]);
  };

  const edit = async (partner: any) => {
    const edited: any = await PartnerRegister({
      initialValues: partner,
    });

    if (!edited) return;

    const target = values.partners.find(({ id }) => id === edited.id);
    const merge = Object.assign({}, target, edited);

    setFieldValue("partners", [
      ...values.partners.filter(({ id }) => id !== merge.id),
      merge,
    ]);
  };

  const remove = async (partner: any) => {
    const confirm = await Modals.Confirmation({
      icon: "alert-triangle",
      title: "Remover sócio",
      content: `Deseja realmente remover o <b>${partner.name}</b> do quadro societário?`,
      submitText: "Remover sócio",
    });

    if (!confirm) return;

    setFieldValue(
      "partners",
      values.partners.filter(({ id }) => id !== partner.id)
    );
  };

  const addResponsible = async () => {
    const person: any = await ResponsiblePersonRegister();

    if (!person) return;

    setFieldValue("responsible", person);
  };

  const editResponsible = async () => {
    const edit: any = await ResponsiblePersonRegister({
      initialValues: values.responsible,
    });

    if (!edit) return;

    setFieldValue("responsible", edit);
  };

  useEffect(() => {
    if (debouncedZipcode && !errors.address?.zipcode) getAddress();
  }, [debouncedZipcode]); // eslint-disable-line

  return (
    <>
      <Grid columns={["auto", "repeat(2, 1fr)"]} gap={[".5rem", "1rem"]}>
        <Forms.InputGroup
          name="name"
          label="Razão social"
          placeholder="Digite a razão social"
        />

        <Forms.InputGroup
          name="foundation_date"
          label="Data de fundação"
          placeholder="Digite a data de fundação"
          mask="date"
        />

        <Forms.InputGroup
          name="phone"
          label="Telefone/celular"
          placeholder="Digite o telefone/celular"
          mask="phone"
        />

        <Forms.SelectGroup
          name="incomes"
          label="Renda/Faturamento mensal"
          options={incomes}
        />

        <Forms.SelectGroup
          name="net_equity"
          label="Patrimônio"
          options={assets}
        />

        <Forms.SelectGroup
          name="type"
          label="Tipo de empresa"
          options={[
            {
              label: "Selecione o tipo de empresa",
              value: "",
            },
            {
              label: "Pública",
              value: "Pública",
            },
            {
              label: "Privada",
              value: "Privada",
            },
          ]}
        />

        <Forms.InputGroup
          name="activity"
          label="Atividade CNAE"
          placeholder="Digite a atividade CNAE"
        />

        <Forms.InputGroup
          name="municipal_inscription"
          label="Inscrição municipal"
          placeholder="Digite a inscrição municipal"
        />

        <Forms.SelectGroup
          name="simples_nacional"
          label="Optante pelo Simples Nacional"
          options={[
            {
              label: "Selecione se é optante pelo Simples Nacional",
              value: "",
            },
            {
              label: "Sim",
              value: "SIM",
            },
            {
              label: "Não",
              value: "NAO",
            },
          ]}
        />
      </Grid>

      <Divider title="Endereço" />

      <Grid columns={["auto", "repeat(2, 1fr)"]} gap={[".5rem", "1rem"]}>
        <Forms.InputGroup
          name="address.zipcode"
          label="CEP"
          placeholder="Digite o CEP"
          mask="zipcode"
          onKeyUp={(event) => {
            setZipcode(event.target.value);
          }}
          action={{
            icon: "edit",
            handler: () => getAddress(true),
          }}
        />
      </Grid>

      <Grid space={[".5rem 0 0", "1rem 0 0"]}>
        <Address {...values.address} />
      </Grid>

      <Divider title="Administrador/Procurador/Representante legal" />

      {!!errors.responsible && (
        <Grid gap="1rem" space={["0 0 1rem", "0 0 2rem"]}>
          <Content.Message kind="danger">
            Por favor preencha uma pessoa física com <b>poderes</b> para assinar
            pela
            <b> estipulante</b>.
          </Content.Message>
        </Grid>
      )}

      <Conditional when={!!values.responsible.name}>
        <S.Responsible.Card>
          <S.Responsible.Heading>
            <S.Responsible.Title>
              <S.Responsible.Icon name="person" /> {values.responsible.name}
            </S.Responsible.Title>

            <S.Responsible.Heading>
              <S.Responsible.Actions>
                <S.Responsible.Action onClick={editResponsible}>
                  Editar
                </S.Responsible.Action>
              </S.Responsible.Actions>
            </S.Responsible.Heading>
          </S.Responsible.Heading>

          <S.Responsible.Body>
            <S.Responsible.Item>
              <S.Responsible.Label>
                <S.Responsible.Icon name="document" /> CPF
              </S.Responsible.Label>

              <S.Responsible.Value>
                {values.responsible.cpf}
              </S.Responsible.Value>
            </S.Responsible.Item>

            <S.Responsible.Item>
              <S.Responsible.Label>
                <S.Responsible.Icon name="mail" /> E-mail
              </S.Responsible.Label>

              <S.Responsible.Value>
                {values.responsible.email}
              </S.Responsible.Value>
            </S.Responsible.Item>

            <S.Responsible.Item>
              <S.Responsible.Label>
                <S.Responsible.Icon name="navigate-circle" /> Endereço
              </S.Responsible.Label>

              <S.Responsible.Value>
                {values.responsible.address.zipcode} -{" "}
                {values.responsible.address.street}, Nº{" "}
                {values.responsible.address.number} -{" "}
                {values.responsible.address.neighborhood}
              </S.Responsible.Value>

              <Conditional when={!!values.responsible.address.complement}>
                <S.Responsible.Value>
                  {values.responsible.address.complement}
                </S.Responsible.Value>
              </Conditional>

              <S.Responsible.Value>
                {values.responsible.address.city},{" "}
                {values.responsible.address.state}
              </S.Responsible.Value>
            </S.Responsible.Item>

            <Conditional when={values.responsible.pep.enabled}>
              <S.Responsible.Item>
                <S.Responsible.Label>
                  <S.Responsible.Icon name="people" /> Pessoa exposta
                  politicamente
                </S.Responsible.Label>

                <S.Responsible.Value>
                  {values.responsible.pep.relationship_degree}
                </S.Responsible.Value>
              </S.Responsible.Item>
            </Conditional>

            <Conditional when={values.responsible.foreigner.enabled}>
              <S.Responsible.Item>
                <S.Responsible.Label>
                  <S.Responsible.Icon name="map" /> Estrangeiro
                </S.Responsible.Label>

                <S.Responsible.Value>
                  {values.responsible.foreigner.country}
                </S.Responsible.Value>
              </S.Responsible.Item>
            </Conditional>
          </S.Responsible.Body>
        </S.Responsible.Card>
      </Conditional>

      <Conditional when={!values.responsible.name}>
        <Button
          size="block"
          type="button"
          kind="ghost"
          onClick={addResponsible}
        >
          Adicionar representante legal
        </Button>
      </Conditional>

      <Divider title="Quadro societário (opcional)" />

      <Conditional when={!!values?.partners?.length}>
        <Grid gap={[".5rem", "1rem"]} columns={["auto", "repeat(2, 1fr)"]}>
          {values?.partners.map?.((partner) => (
            <S.Partner.Item>
              <S.Partner.Details>
                <S.Partner.Title>{partner.name}</S.Partner.Title>
                <S.Partner.Description>
                  {partner.document}
                </S.Partner.Description>
                <S.Partner.Description>
                  {partner.percentage.replace(/\D/g, "")}% de participação
                </S.Partner.Description>
              </S.Partner.Details>

              <S.Partner.Actions>
                <S.Partner.Action kind="ghost" onClick={() => edit(partner)}>
                  Editar
                </S.Partner.Action>

                <S.Partner.Action
                  kind="ghost"
                  variant="danger"
                  onClick={() => remove(partner)}
                >
                  Remover
                </S.Partner.Action>
              </S.Partner.Actions>
            </S.Partner.Item>
          ))}
        </Grid>
      </Conditional>

      <Conditional when={props.rules.maxPartnerPercentage()}>
        <Shake>
          <Grid space={[".5rem 0 0", "1rem 0 0"]}>
            <Content.Message kind="danger">
              A soma da porcentagem de todos os sócios <b>não podem</b>{" "}
              ultrapassar <b>100%</b>, ajuste para continuar.
            </Content.Message>
          </Grid>
        </Shake>
      </Conditional>

      <Conditional when={values.partners.length < 3}>
        <Grid space={[".5rem 0 0", "1rem 0 0"]}>
          <Button kind="ghost" type="button" onClick={add}>
            Cadastrar {values.partners.length + 1}/3 sócio
          </Button>
        </Grid>
      </Conditional>
    </>
  );
};
